import { useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { processEntityDataFromApi } from "@/lib/utils/crm/task-data-processing";
import { prepareEntityGraphQlMutationInput } from "@/lib/utils/crm/prepare-graphql-entity-payload";
import {
  GetContactssDocument,
  GetContactsDocument,
  CreateContactsDocument,
  UpdateContactsDocument,
  DeleteContactsDocument,
  GetContactssQuery,
  GetContactsQuery,
  CreateContactsMutation,
  UpdateContactsMutation,
  DeleteContactsMutation,
  ContactsInsertInput,
  ContactsFilter,
  Contacts,
} from "@/src/gql/graphql";
import { ENHANCED_CONTACTS_CONFIG } from "@/features/contacts/components/EnhancedContactsConfig";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";
// Contact/company writes go through pg_graphql, not an API route, so the
// server-side mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

function parseContactFromApi(node: Contacts): Contacts {
  return processEntityDataFromApi(node, ENHANCED_CONTACTS_CONFIG.fields);
}

/**
 * Add "not a company line" to whatever filter the caller built.
 *
 * A COMPANY LINE ANCHOR is a `contacts` row that exists only to hold a shared
 * front desk's history — it is not a person (see CONTACT_METHOD_EXCLUSIVITY).
 * Excluding it in the FILTER rather than after the fetch matters: this query's
 * `totalCount` is the contact count the CRM displays, and a client-side drop
 * would leave the count inflated and the page sizes short. The partial index
 * `idx_contacts_is_company_line` covers the predicate.
 *
 * A caller that explicitly filters on `is_company_line` (the promote-to-anchor
 * admin view) is left alone.
 */
function excludeCompanyLineAnchors(
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const existing = params?.filter;
  const filter: ContactsFilter =
    existing && typeof existing === "object" ? (existing as ContactsFilter) : {};
  if (filter.is_company_line) return params;
  return { ...params, filter: { ...filter, is_company_line: { eq: false } } };
}

/**
 * `usePaginatedCollection` lists these among its result-memo dependencies, so
 * they are MODULE-LEVEL constants rather than inline arrows — an arrow
 * allocated per render would make that memo a no-op (MEMO_STABLE_PROPS).
 */
const selectContactsConnection = (data: GetContactssQuery) => data.contactsCollection;
const mapContactNode = (node: unknown): Contacts => parseContactFromApi(node as Contacts);

/**
 * Contact hooks using generated GraphQL operations.
 *
 * The `EntityHooks` object is itself a dependency of `useBuildActionContext`'s
 * `useMemo`, and none of its members close over anything render-scoped — so it
 * is allocated ONCE, at module scope. (A `useMemo` inside the factory would
 * also be stable, but call sites that already wrap the factory —
 * `useMemo(() => useContactHooks(), [])` in `app/contacts/page.tsx` — would then
 * skip the inner hook on re-render and break hook order. A module constant is
 * correct under every call pattern.)
 */
const CONTACT_ENTITY_HOOKS: EntityHooks<Contacts> = {
  queryDocument: GetContactssDocument,
  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetContactssDocument,
      excludeCompanyLineAnchors(params),
      selectContactsConnection,
      mapContactNode
    ),
  useInfinite: () => EMPTY_INFINITE_RESULT,
  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetContactsQuery>(GetContactsDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || options?.enabled === false,
    });
    return useMemo(
      () => ({
        data: result.data?.contactsCollection?.edges?.[0]?.node
          ? parseContactFromApi(result.data.contactsCollection.edges[0].node as Contacts)
          : undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      }),
      [result]
    );
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateContactsMutation>(UpdateContactsDocument);

    const mutateAsync = useCallback(
      async ({ id, ...input }: { id: string } & Partial<Contacts>) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          ENHANCED_CONTACTS_CONFIG.fields,
          { mode: "update" }
        );
        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the contact.");

        const updated = data?.updatecontactsCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the contact.");
        return parseContactFromApi(updated as Contacts);
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteContactsMutation>(DeleteContactsDocument);
    const mutateAsync = useCallback(
      async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the contact.");
        const deletedId = data?.deleteFromcontactsCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the contact.");
        return deletedId;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },
  useCreate: () => {
    const [mutate, result] = useMutation<CreateContactsMutation>(CreateContactsDocument);
    const mutateAsync = useCallback(
      async (input: ContactsInsertInput) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          ENHANCED_CONTACTS_CONFIG.fields,
          { mode: "insert" }
        );
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the contact.");
        const created = data?.insertIntocontactsCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the contact.");
        return created as Contacts;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },

  getFieldConfig: (fieldName: string) => {
    return ENHANCED_CONTACTS_CONFIG.fields[fieldName] || null;
  },

  getTableConfig: () => {
    return ENHANCED_CONTACTS_CONFIG;
  },
};

export const useContactHooks = (): EntityHooks<Contacts> => CONTACT_ENTITY_HOOKS;
